import { PlusOutlined } from '@ant-design/icons'
import { Button, Card, Flex, message, Tabs } from 'antd'
import {
  BoosterMilestoneDetail,
  BoosterMilestoneFormValues,
  BoosterProgramFormValues,
} from 'Modules'
import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'

import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BOOSTER_PROGRAM_MAX_MILESTONES } from 'constants/booster-program'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import {
  useAddBoosterMilestones,
  useBoosterProgramDetail,
  useBoosterProgramDetailStats,
  useChangeBoosterProgramStatus,
  useDeleteBoosterMilestone,
  useUpdateBoosterMilestone,
  useUpdateBoosterProgram,
  useUploadBoosterProgramWhitelist,
} from 'hooks/booster-program'
import { usePermission } from 'hooks/use-permission'
import { BoosterEnrolledUsersTable } from 'modules/booster-program/detail/enrolled-users-table'
import { BoosterMilestoneCompletionCard } from 'modules/booster-program/detail/milestone-completion-card'
import { MilestoneEditForm } from 'modules/booster-program/detail/milestone-edit-form'
import { BoosterMilestoneSummaryCard } from 'modules/booster-program/detail/milestone-summary-card'
import { BoosterDetailOverviewStats } from 'modules/booster-program/detail/overview-stats'
import { BoosterProgramSettingsView } from 'modules/booster-program/detail/program-settings-view'
import { BoosterProgramStatusCard } from 'modules/booster-program/detail/program-status-card'
import { getApiErrorMessage } from 'utils/api-error'

import '../styles.scss'

const blankNewMilestone = (index: number): BoosterMilestoneDetail => ({
  id: 'new',
  index,
  threshold: 1,
  rewardType: 'DISCOUNT_CODE',
  codeExpiry: '30_days',
  totalSupply: 0,
  issued: 0,
  remaining: 0,
  linkedPtos: [],
})

const BoosterProgramDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.BOOSTER_PROGRAM)
  const { detail } = useBoosterProgramDetail(id)
  const updateProgram = useUpdateBoosterProgram()
  const uploadWhitelist = useUploadBoosterProgramWhitelist()
  const changeStatus = useChangeBoosterProgramStatus()
  const addMilestonesMutation = useAddBoosterMilestones()
  const updateMilestoneMutation = useUpdateBoosterMilestone()
  const deleteMilestoneMutation = useDeleteBoosterMilestone()
  const { stats, milestoneCompletion } = useBoosterProgramDetailStats(id)

  const [isAddingMilestone, setIsAddingMilestone] = useState(false)
  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null)
  const milestones = detail?.milestones ?? []
  const someoneEditing = isAddingMilestone || editingMilestoneId !== null

  const handleSaveMilestone = useCallback(
    async (milestoneId: string, values: BoosterMilestoneFormValues) => {
      if (!id) return
      try {
        await updateMilestoneMutation.mutateAsync({ programId: id, milestoneId, values })
        message.success('Milestone updated')
        // Detail query is invalidated by the mutation hook — local milestones
        // state refreshes via the useEffect once the refetch lands.
      } catch (err) {
        message.error(getApiErrorMessage(err, 'Failed to update milestone'))
      }
    },
    [id, updateMilestoneMutation]
  )

  const handleRemoveMilestone = useCallback(
    async (milestoneId: string) => {
      if (!id) return
      try {
        await deleteMilestoneMutation.mutateAsync({ programId: id, milestoneId })
        message.success('Milestone removed')
      } catch (err) {
        message.error(getApiErrorMessage(err, 'Failed to remove milestone'))
      }
    },
    [id, deleteMilestoneMutation]
  )

  const handleSubmitNewMilestone = useCallback(
    async (values: BoosterMilestoneFormValues) => {
      if (!id) return
      try {
        await addMilestonesMutation.mutateAsync({ programId: id, milestones: [values] })
        message.success('Milestone added')
        setIsAddingMilestone(false)
        // Detail query is invalidated by the mutation hook — milestones state
        // refreshes via the useEffect above once the refetch lands.
      } catch (err) {
        message.error(getApiErrorMessage(err, 'Failed to add milestone'))
      }
    },
    [id, addMilestonesMutation]
  )

  const handleSaveProgramSettings = useCallback(
    async (values: BoosterProgramFormValues) => {
      if (!id) return
      try {
        await updateProgram.mutateAsync({ id, values })
        // Replace the program's whitelist when a new file was selected (targetUser = "others").
        if (values.targetUsers === 'OTHERS' && values.whitelistFile) {
          await uploadWhitelist.mutateAsync({ programId: id, file: values.whitelistFile })
        }
        message.success('Program settings updated')
      } catch (err) {
        message.error(getApiErrorMessage(err, 'Failed to update program settings'))
      }
    },
    [id, updateProgram, uploadWhitelist]
  )

  const handleActivate = useCallback(async () => {
    if (!id) return
    try {
      await changeStatus.mutateAsync({ id, status: 'ACTIVE' })
      message.success('Program resumed')
    } catch (err) {
      message.error(getApiErrorMessage(err, 'Failed to resume program'))
    }
  }, [id, changeStatus])

  const handlePause = useCallback(async () => {
    if (!id) return
    try {
      await changeStatus.mutateAsync({ id, status: 'PAUSED' })
      message.success('Program paused')
    } catch (err) {
      message.error(getApiErrorMessage(err, 'Failed to pause program'))
    }
  }, [id, changeStatus])

  const breadCrumbItems = [BREADCRUMBS.BOOSTER_PROGRAM, BREADCRUMBS.BOOSTER_PROGRAM_DETAIL]

  if (!detail || !id) {
    return (
      <Flex vertical gap={24} className="booster-program">
        <Breadcrumb additionalItems={breadCrumbItems} />
      </Flex>
    )
  }

  const canEditConfig = canEdit && detail.status === 'SCHEDULED'

  const overviewContent = (
    <Flex vertical gap={24}>
      <BoosterDetailOverviewStats stats={stats} />
      <BoosterMilestoneCompletionCard data={milestoneCompletion} />
      <BoosterProgramStatusCard
        status={detail.status}
        isActive={detail.isActive}
        message={detail.statusMessage}
        pausedAt={detail.pausedAt}
        pausedBy={detail.pausedBy}
        onActivate={canEdit ? handleActivate : undefined}
        onPause={canEdit ? handlePause : undefined}
      />
      <BoosterProgramSettingsView
        data={detail}
        onSave={canEditConfig ? handleSaveProgramSettings : undefined}
      />

      <Card className="booster-program-create__card">
        <div className="booster-detail__section-title">
          Milestones &amp; Promo Code Configuration
        </div>
        {milestones.map((m, i) => (
          <BoosterMilestoneSummaryCard
            key={m.id}
            milestone={m}
            canEdit={canEditConfig}
            canRemove={canEditConfig && milestones.length > 1}
            previousThreshold={milestones[i - 1]?.threshold}
            nextThreshold={milestones[i + 1]?.threshold}
            isEditing={editingMilestoneId === m.id}
            lockEdit={someoneEditing && editingMilestoneId !== m.id}
            onStartEdit={() => setEditingMilestoneId(m.id)}
            onStopEdit={() => setEditingMilestoneId(null)}
            onSave={canEditConfig ? handleSaveMilestone : undefined}
            onRemove={handleRemoveMilestone}
          />
        ))}
        {isAddingMilestone && (
          <MilestoneEditForm
            milestone={blankNewMilestone(milestones.length)}
            index={milestones.length}
            getPreviousThreshold={() => milestones[milestones.length - 1]?.threshold}
            onCancel={() => setIsAddingMilestone(false)}
            onSave={handleSubmitNewMilestone}
          />
        )}
        {canEditConfig && !someoneEditing && milestones.length < BOOSTER_PROGRAM_MAX_MILESTONES && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            className="booster-program-create__add-milestone"
            block
            onClick={() => setIsAddingMilestone(true)}
          >
            Add Milestone
          </Button>
        )}
      </Card>
    </Flex>
  )

  return (
    <Flex vertical gap={24} className="booster-program">
      <Breadcrumb additionalItems={breadCrumbItems} />

      <div>
        <div className="main-title" style={{ marginBottom: 8 }}>
          Booster Program Details
        </div>
        <div className="main-description">Manage milestone rewards and track user progress</div>
      </div>

      <Tabs
        defaultActiveKey="overview"
        destroyInactiveTabPane
        items={[
          {
            key: 'overview',
            label: 'Overview & Configuration',
            children: overviewContent,
          },
          {
            key: 'enrolled',
            label: 'Enrolled Users',
            children: <BoosterEnrolledUsersTable programId={id} milestones={milestones} />,
          },
        ]}
      />
    </Flex>
  )
}

export default BoosterProgramDetailPage
